/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 *
 *  Portal - Location Profile View
 */
import React from 'react';
import { Redirect, Switch, Route, useParams, matchPath } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStores } from 'stores/RootStore';

// Design System
import { makeStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import CircularProgress from '@material-ui/core/CircularProgress';

import TabPanel from 'components/LocationProfile/TabPanel';
import TabLabel from 'components/LocationProfile/TabLabel';
import Profile from 'components/LocationProfile/Profile';
import Users from 'components/LocationProfile/Users';
import BadgeHistory from 'components/LocationProfile/BadgeHistory';

const tabRouter = {
  0: {
    tabIndex: 0,
    match: '/location/:id/profile',
    route: 'profile',
  },
  1: {
    tabIndex: 1,
    match: '/location/:id/users',
    route: 'users',
  },
  2: {
    tabIndex: 2,
    match: '/location/:id/badge-history',
    route: 'badge-history',
  },
};

const LocationProfile = props => {
  const classes = styles();
  const stores = useStores();
  const { isLoading } = stores.locationStore;
  const { id } = useParams();
  const [value, setTab] = React.useState(0);

  const handleChange = (event, tab) => {
    event.preventDefault();
    props.history.push(tabRouter[tab].route);
  };

  const routeToTab = () => {
    Object.keys(tabRouter).forEach(key => {
      const match = matchPath(props.location.pathname, {
        path: tabRouter[key].match,
        exact: true,
        strict: false,
      });

      if (match) {
        if (value !== tabRouter[key].tabIndex) {
          setTab(tabRouter[key].tabIndex);
        }
      }
    });
  };

  // If id is undefined, route to home
  if (id === 'undefined') {
    return <Redirect to='/' />;
  }

  // Set page title
  stores.appStore.setPage('Location');

  // Set tab based on route
  routeToTab();

  // Check Store
  if (!isLoading && stores.locationStore.location.id !== id && id !== 'undefined') {
    stores.locationStore.setLocation(id);
  }

  return (
    <div className={classes.root}>
      <Tabs
        orientation={isWidthUp('sm', props.width) ? 'vertical' : 'horizontal'}
        indicatorColor='primary'
        value={value}
        onChange={handleChange}
        className={classes.tabs}
      >
        <Tab label={<TabLabel label='Profile' variant='inherit' />} />
        <Tab label={<TabLabel label='Users' variant='inherit' />} />
        <Tab label={<TabLabel label='Badge History' variant='inherit' />} />
      </Tabs>
      <Switch>
        <Route path='/location/:id/profile'>
          <TabPanel title='Location Profile' value={value} index={0} className={classes.tabPanel}>
            {isLoading ? <CircularProgress size={24} className={classes.circularProgress} /> : <Profile {...props} />}
          </TabPanel>
        </Route>
        <Route path='/location/:id/users'>
          <TabPanel title='Location Users' value={value} index={1} className={classes.tabPanel}>
            <Users {...props} />
          </TabPanel>
        </Route>
        <Route path='/location/:id/badge-history'>
          <TabPanel title='Badge History' value={value} index={2} className={classes.tabPanel}>
            <BadgeHistory id={id} {...props} />
          </TabPanel>
        </Route>
      </Switch>
    </div>
  );
};

const styles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    [theme.breakpoints.up('sm')]: {
      flexGrow: 1,
      display: 'flex',
      minHeight: '400px',
      height: '100%',
    },
  },
  tabs: {
    minHeight: '100%',
    minWidth: '140px',
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingTop: '15px',
    [theme.breakpoints.down('xs')]: {
      borderRight: '0px',
    },
  },
}));

export default withWidth()(observer(LocationProfile));
